import React from "react";
import { ShieldCheck, Network, Activity, Globe2, Database, Users2 } from "lucide-react";

// Abstract acquisition-intelligence dashboard. No fake metrics presented as real data.
export default function HeroDashboard() {
  return (
    <div className="relative w-full" data-testid="hero-dashboard-visual">
      <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-cyan-400" />
            <span className="text-[11px] uppercase tracking-[0.18em] text-white/60 font-mono-pr">Acquisition Intelligence</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[11px] text-white/40 font-mono-pr">
            <span>v.live</span><span className="opacity-50">·</span><span>compliance ok</span>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3 p-4 lg:p-5">
          {/* Audience segments */}
          <Panel className="col-span-7 lg:col-span-7 h-40" title="Audience Segments" icon={Users2}>
            <SegmentBars />
          </Panel>

          {/* GEO */}
          <Panel className="col-span-5 lg:col-span-5 h-40" title="GEO Distribution" icon={Globe2}>
            <GeoList />
          </Panel>

          {/* Traffic sources */}
          <Panel className="col-span-6 lg:col-span-4 h-36" title="Traffic Sources" icon={Network}>
            <SourcesRing />
          </Panel>

          {/* Performance */}
          <Panel className="col-span-6 lg:col-span-5 h-36" title="Campaign Performance" icon={Activity}>
            <SparkLine />
          </Panel>

          {/* Compliance */}
          <Panel className="col-span-12 lg:col-span-3 h-36" title="Compliance" icon={ShieldCheck}>
            <ComplianceList />
          </Panel>
        </div>

        {/* Bottom rail */}
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-between text-[11px] text-white/45 font-mono-pr">
          <div className="flex items-center gap-2"><Database className="h-3.5 w-3.5 text-cyan-400" /> Partner network · synced</div>
          <div className="hidden md:flex items-center gap-3">
            <span>EU</span><span>UK</span><span>LATAm</span><span>APAC</span>
          </div>
        </div>
      </div>

      {/* Glow */}
      <div aria-hidden className="absolute -inset-px rounded-2xl pointer-events-none" style={{ boxShadow: "0 40px 120px -30px rgba(0,82,255,0.45)" }} />
    </div>
  );
}

function Panel({ className = "", title, icon: Icon, children }) {
  return (
    <div className={`relative rounded-lg border border-white/10 bg-[#0F172A]/40 p-3 overflow-hidden ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-3.5 w-3.5 text-cyan-400" />
        <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-mono-pr">{title}</span>
      </div>
      <div className="relative h-[calc(100%-1.25rem)]">{children}</div>
    </div>
  );
}

function SegmentBars() {
  const segs = [
    { label: "High Intent", v: 78 },
    { label: "Returning", v: 62 },
    { label: "Lookalike", v: 54 },
    { label: "Reactivation", v: 41 },
    { label: "Cold", v: 28 },
  ];
  return (
    <div className="space-y-2">
      {segs.map((s, i) => (
        <div key={s.label} className="flex items-center gap-2">
          <div className="text-[11px] text-white/60 w-24 truncate">{s.label}</div>
          <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${s.v}%`,
                background: i === 0 ? "linear-gradient(90deg,#06B6D4,#0052FF)" : "rgba(255,255,255,0.35)",
                transition: "width 800ms ease",
              }}
            />
          </div>
          <div className="text-[10.5px] text-white/45 font-mono-pr w-8 text-right">{s.v}</div>
        </div>
      ))}
    </div>
  );
}

function GeoList() {
  const rows = [
    { code: "UK", v: 32 },
    { code: "DE", v: 24 },
    { code: "CA", v: 18 },
    { code: "BR", v: 14 },
    { code: "JP", v: 12 },
  ];
  return (
    <div className="grid grid-cols-5 gap-1.5 h-full items-end">
      {rows.map((r) => (
        <div key={r.code} className="flex flex-col items-center justify-end gap-1.5">
          <div className="w-full rounded-sm" style={{ height: `${r.v * 2.2}px`, background: "linear-gradient(180deg,#0052FF, #06B6D4)" }} />
          <span className="text-[10px] text-white/55 font-mono-pr">{r.code}</span>
        </div>
      ))}
    </div>
  );
}

function SourcesRing() {
  // Donut using stroke-dashoffset
  const slices = [
    { v: 42, c: "#0052FF" },
    { v: 28, c: "#06B6D4" },
    { v: 18, c: "#7DD3FC" },
    { v: 12, c: "#475569" },
  ];
  const r = 28;
  const C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="flex items-center gap-3 h-full">
      <svg viewBox="0 0 80 80" className="h-20 w-20 -rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
        {slices.map((s, i) => {
          const len = (s.v / 100) * C;
          const dasharray = `${len} ${C - len}`;
          const dashoffset = -offset;
          offset += len;
          return (
            <circle key={i} cx="40" cy="40" r={r} fill="none" stroke={s.c} strokeWidth="10" strokeDasharray={dasharray} strokeDashoffset={dashoffset} strokeLinecap="butt" />
          );
        })}
      </svg>
      <div className="flex-1 space-y-1.5 text-[11px]">
        {["Affiliate", "Direct", "Partner", "Other"].map((l, i) => (
          <div key={l} className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-sm" style={{ backgroundColor: slices[i].c }} />
            <span className="text-white/65">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SparkLine() {
  const pts = [10, 14, 12, 18, 22, 19, 26, 28, 25, 32, 36, 34, 40];
  const max = 44;
  const w = 220, h = 76;
  const step = w / (pts.length - 1);
  const d = pts.map((p, i) => `${i === 0 ? "M" : "L"}${i * step},${h - (p / max) * h}`).join(" ");
  const dFill = `${d} L${w},${h} L0,${h} Z`;
  return (
    <div className="relative h-full">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id="spg" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={dFill} fill="url(#spg)" />
        <path d={d} fill="none" stroke="#06B6D4" strokeWidth="1.6" />
      </svg>
      <div className="absolute right-1 top-0 text-[10.5px] text-white/55 font-mono-pr">7d trend</div>
    </div>
  );
}

function ComplianceList() {
  const rows = [
    "Sender ID set",
    "Suppression synced",
    "Rights documented",
    "Partner reviewed",
  ];
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r} className="flex items-center gap-2 text-[11px] text-white/65">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
          <span>{r}</span>
        </li>
      ))}
    </ul>
  );
}
